import '../styles/ContractLibrary.css';

import { useEffect, useState, useContext } from 'react';
import { client } from '../middleware/ShopifyClient.jsx';

import { useNavigation } from '../middleware/Navigation.jsx';
import { CartContext } from '../middleware/TrackCart.jsx';

import updateDocumentTitle from '../utils/updateDocumentTitle.js';

const ContractItem = ({ item }) => {
  const node = item.node;
  const { addItemToCart, removeItemFromCart, isItemInCart } = useContext(CartContext);
  const navigate = useNavigation();

  const viewContractDetails = () => {
    navigate(`/contract/${node.handle}`);
  };

  const AddToCart = (event) => {
    event.stopPropagation();
    addItemToCart(node);
  };

  const RemoveFromCart = (event) => {
    event.stopPropagation();
    removeItemFromCart(node.id);
  };

  const imageUrl = node.images?.edges?.[0]?.node?.url || '/images/contract.png';
  const hoverImageUrl = node.images.edges?.[1].node.url || '/images/contract.png';

  return (
    <div className="contract-item" onClick={viewContractDetails}>
      <div className="contract-image">
        <img src={imageUrl} alt={node.title} loading='lazy' />
        <img src={hoverImageUrl} alt={node.title} loading='lazy' />
        <div className="contract-overlay">
          <span>View Details</span>
        </div>
      </div>
      <div className="contract-details">
        <h3>{node.title}</h3>
        <div className="contract-price-action">
          {isItemInCart(node.id) ? (
            <button className="in-cart-btn" onClick={RemoveFromCart}>
              Remove
            </button>
          ) : (
            <button className="add-to-cart-btn" onClick={AddToCart}>
              Add to Cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ContractLibrary = () => {
  const [contracts, setProducts] = useState([]);
  const [endCursor, setEndCursor] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loading, setLoading] = useState(false);

  updateDocumentTitle('Shop Contracts');

  const fetchProducts = async (cursor = null) => {
    setLoading(true);

    const query = `
      {
        products(first: 20${cursor ? `, after: "${cursor}"` : ''}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
                  node {
                    id
                    title
                    handle
                    description
                    variants(first: 1) {
                      edges {
                        node {
                          id
                        }
                      }
                    }
                    images(first: 2) {
                      edges {
                        node {
                          url
                        }
                      }
                    }
                  }
                }
        }
      }
    `;

    try {
      const response = await client.request(query);

      const newProducts = response?.data?.products?.edges || [];
      const pageInfo = response?.data?.products?.pageInfo;

      setProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p.node.id));
        const uniqueNew = newProducts.filter((p) => !existingIds.has(p.node.id));
        return [...prev, ...uniqueNew];
      });
      setEndCursor(pageInfo.endCursor);
      setHasNextPage(pageInfo.hasNextPage);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]); // Set empty products array on error
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <div className="library-page">
      <div className="library-title-section">
        <div className="container">
          <h2>Contract Library</h2>
          <p className="subtitle">Find the perfect contract template for your business.</p>
        </div>
      </div>
      
      <div className="library-main">
        <div className="intro-text-section">
          <div className="container">
            <h2>Legal Protection for Every Entrepreneur</h2>
            <p>Your hustle deserves real protection without the hefty legal bill. Our lawyer-grade templates give freelancers, startups, and small businesses the perfect balance: legally sound protection with plain-language guidance you can actually understand.</p>
            <p>Unlike free templates that lack enforceability or expensive legal services that drain resources, our contracts are ready to use in minutes.</p>
            <p><strong>Because what you're building matters. And it deserves to be protected.</strong></p>
          </div>
        </div>
        
        <div className="contracts-list-container">
          <h2>Available Contract Templates</h2>
          
          {contracts.length === 0 && loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading templates...</p>
            </div>
          ) : contracts.length === 0 ? (
            <div className="no-contracts">
              <p>No contracts available at the moment. Please check back later.</p>
            </div>
          ) : (
            <div className="contracts-list">
              {contracts.map(contract => <ContractItem key={contract.node.handle} item={contract} />)}
            </div>
          )}
          
          {hasNextPage && contracts.length > 0 && (
            <div className="load-more-container">
              <button 
                className="load-more-btn" 
                onClick={() => fetchProducts(endCursor)} 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Loading...
                  </>
                ) : (
                  'Load More Templates'
                )}
              </button>
            </div>
          )}
        </div>
        
        <div className="library-timer-section">
          <div className="timer-message">
            <h2>Instant Access, Immediate Protection</h2>
            <p>Download in Microsoft Word format instantly, customize with our cheat sheet, and start using your contract the same day.</p>
          </div>
          <div className="timer-image">
            <img src="/images/group.png" alt="Lawyers together" className="refined-image" />
          </div>
        </div>
        
        <div className="features-section">
          <h2>Why Choose ContractWell Templates</h2>
          <div className="features-container">
            <div className="feature">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path fill="#053C26" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                </svg>
              </div>
              <h3>Legally Thorough</h3>
              <p>Built with the legal depth and clarity that generic templates lack.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path fill="#053C26" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
              </div>
              <h3>Plain-English Cheat Sheets</h3>
              <p>Every template includes explanations of key legal terms, so you're never left guessing what you're signing.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path fill="#053C26" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                </svg>
              </div>
              <h3>Fully Customizable</h3>
              <p>Complete in minutes, reuse forever. Save time, money, and legal fees while protecting your business.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractLibrary; 